import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { Ionicons } from "@expo/vector-icons";
import BottomSheet, {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetScrollView,
} from "@gorhom/bottom-sheet";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { remapProps } from "react-native-css-interop";
import ClockIcon from "@/assets/svgs/clock-icon";
import CloseIcon from "@/assets/svgs/close-icon";
import RemoteIcon from "@/assets/svgs/remote-icon";
import ReportIcon from "@/assets/svgs/report-icon";
import SubjectIcon from "@/assets/svgs/subject-icon";
import UserIcon from "@/assets/svgs/user-icon";
import { useFormatDate } from "@/elements/hooks/use-format-date";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import { useSheet } from "../shared/sheet-provider";
import { Avatar } from "../ui/avatar";
import { Button } from "../ui/button";
import type { api as apiType } from "../utils/convex-api";
import { api } from "../utils/convex-api";
import { contactSupport } from "../utils/helpers";
import { SheetIds } from "../utils/sheet-ids";

remapProps(BottomSheetScrollView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

export type TutorLessonSheetPayload = {
  lessonData: NonNullable<
    typeof apiType.tutor.home.queries.getSessions._returnType
  >[0];
};

export type TutorLessonSheetResult = {
  joinLesson: boolean;
  cancelLesson: boolean;
  reportNoShow: boolean;
  reportStudentNoShow: boolean;
};

const ONE_HOUR_MS = 3600000;

function TutorLessonSheet() {
  const [index, setIndex] = useState(-1);
  const [shouldRender, setShouldRender] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{
    startTimeUTC: number;
    endTimeUTC: number;
  } | null>(null);
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);

  const fmt = useFormatDate();

  const { register, close, dismiss, sheet } = useSheet<
    TutorLessonSheetPayload,
    TutorLessonSheetResult
  >(SheetIds.TUTOR_LESSON);

  const responsiveWidth = useResponsiveWidth();
  const { lessonData } = (sheet?.payload ?? {}) as TutorLessonSheetPayload;
  const { width, height } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;
  const maxSheetHeight = height * 0.85;

  const rescheduleCount = lessonData?.rescheduleCount ?? 0;
  const canReschedule =
    lessonData?.status === "scheduled" &&
    lessonData.startTime - Date.now() > ONE_HOUR_MS &&
    rescheduleCount < 2;

  const { data: availableSlots, isLoading: slotsLoading } = useQuery({
    ...convexQuery(
      api.tutor.rescheduleSession.queries.getAvailableSlotsForReschedule,
      showReschedule && lessonData
        ? { sessionId: lessonData.sessionId }
        : "skip",
    ),
  });

  const { mutateAsync: rescheduleSession, isPending: isRescheduling } =
    useMutation({
      mutationFn: useConvexMutation(
        api.tutor.rescheduleSession.mutations.rescheduleSession,
      ),
    });

  useEffect(() => {
    setShouldRender(true);
  }, []);

  useEffect(() => {
    setShowReschedule(false);
    setShowConfirmation(false);
    setSelectedSlot(null);
    setRescheduleError(null);
  }, [lessonData?.sessionId]);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register],
  );

  const handleSheetChanges = useCallback(
    (i: number) => {
      setIndex(i);
      if (i === -1) {
        setTimeout(() => {
          dismiss();
        }, 1000);
      }
    },
    [dismiss],
  );

  const onClose = useCallback(() => {
    close();
  }, [close]);

  const handleConfirmReschedule = useCallback(async () => {
    if (!selectedSlot || !lessonData) return;

    setRescheduleError(null);

    try {
      await rescheduleSession({
        sessionId: lessonData.sessionId,
        newStartTimeUTC: selectedSlot.startTimeUTC,
      });

      setShowReschedule(false);

      setSelectedSlot(null);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to reschedule session";

      setRescheduleError(message);
    }
  }, [selectedSlot, lessonData, rescheduleSession]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose],
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      enableDynamicSizing
      maxDynamicContentSize={maxSheetHeight}
      index={index}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      containerStyle={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      {!!lessonData && (
        <BottomSheetScrollView>
          <View className="bg-teachLoop-creamBase rounded-t-[20]">
            <View className="h-6" />
            <View className="mx-5 items-end">
              <Pressable onPress={onClose} className="active:opacity-80">
                <CloseIcon
                  className="size-6"
                  pathClassName="stroke-teachLoop-textPrimary"
                />
              </Pressable>
            </View>
            <View className="mx-5 gap-6">
              <View className="flex-row items-center gap-4">
                <Avatar size="md">
                  <Avatar.Content
                    label={lessonData.student.name}
                    source={
                      lessonData.student.imageUrl
                        ? { uri: lessonData.student.imageUrl }
                        : undefined
                    }
                    labelSize="md"
                    size="md"
                  />
                </Avatar>
                <View className="flex-1">
                  <Text className="font-geologica-medium font-medium text-black text-xl leading-[1.5]">
                    {lessonData.student.name}
                  </Text>

                  {lessonData.student.email && (
                    <Pressable
                      className="flex-row items-center gap-1 active:opacity-70"
                      onPress={() => {
                        navigator.clipboard.writeText(
                          lessonData.student.email!,
                        );
                      }}
                    >
                      <Text className="font-geologica-regular font-normal text-gray-600 text-sm leading-[1.5]">
                        {lessonData.student.email}
                      </Text>
                      <Ionicons name="copy-outline" size={14} color="#6b7280" />
                    </Pressable>
                  )}
                </View>
              </View>

              <View className="flex-row items-center gap-[6px]">
                {lessonData.deliveryMethod === "in_person" ? (
                  <UserIcon
                    className="size-6"
                    pathClassName={"stroke-gray-800"}
                  />
                ) : (
                  <RemoteIcon
                    className="size-6"
                    pathClassName={"stroke-gray-800"}
                  />
                )}
                <Text
                  className={`font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]`}
                >
                  {lessonData.deliveryMethod === "in_person"
                    ? "In person"
                    : "Remote"}
                </Text>
              </View>
              <View className="gap-2">
                <View className="flex-row items-center gap-[6px]">
                  <SubjectIcon
                    className="size-6"
                    pathClassName={"stroke-gray-800"}
                  />
                  <Text
                    className={`font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]`}
                  >
                    {lessonData.subjectName}
                  </Text>
                </View>
                <Text
                  className={`font-geologica-regular font-normal text-black text-base leading-[1.5]`}
                >
                  {"  "}
                  &bull; {lessonData.yearGroupName}
                </Text>
              </View>

              <View className="gap-2">
                <View className="flex-row items-center gap-[6px]">
                  <ClockIcon
                    className="size-6"
                    pathClassName={"stroke-gray-800"}
                  />
                  <Text
                    className={`font-geologica-regular font-normal text-black text-base leading-[1.5]`}
                  >
                    {fmt(lessonData.startTime, "do MMMM")}{" "}
                    {fmt(lessonData.startTime, "HH:mm")} -{" "}
                    {fmt(lessonData.endTime, "HH:mm")}
                  </Text>
                </View>

                {canReschedule && !showConfirmation && !showReschedule && (
                  <Pressable
                    className="active:opacity-80"
                    onPress={() => setShowConfirmation(true)}
                  >
                    <Text className="font-geologica-medium font-medium text-teachLoop-accentPrimary text-sm leading-[1.5] ml-8">
                      Need to change the time?
                    </Text>
                  </Pressable>
                )}
              </View>

              {showConfirmation && !showReschedule && (
                <View className="gap-3 bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <Text className="font-geologica-regular font-normal text-gray-800 text-sm leading-[1.5]">
                    Please note that you are rescheduling this lesson. Lessons
                    can only be rescheduled a maximum of two times. Before
                    proceeding, ensure you have informed the parent and that
                    they have confirmed they are happy with the change.
                  </Text>
                  <View className="flex-row gap-3">
                    <Button
                      size="sm"
                      outline
                      onPress={() => setShowConfirmation(false)}
                    >
                      <Button.Text size="sm" intent="primary">
                        Go back
                      </Button.Text>
                    </Button>
                    <Button
                      size="sm"
                      className="px-6"
                      onPress={() => {
                        setShowConfirmation(false);
                        setShowReschedule(true);
                      }}
                    >
                      <Button.Text size="sm">
                        I have informed the parent
                      </Button.Text>
                    </Button>
                  </View>
                </View>
              )}

              {showReschedule && (
                <View className="gap-3">
                  <Text className="font-geologica-medium font-medium text-gray-800 text-sm leading-[1.5]">
                    Select a new time
                  </Text>

                  {slotsLoading ? (
                    <View className="py-3 items-center">
                      <ActivityIndicator size="small" color="#2C7A7B" />
                    </View>
                  ) : !availableSlots || availableSlots.length === 0 ? (
                    <Text className="font-geologica-regular font-normal text-gray-500 text-sm leading-[1.5]">
                      No available slots for this day.
                    </Text>
                  ) : (
                    <>
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={{ gap: 8 }}
                      >
                        {availableSlots.map((slot) => {
                          const isSelected =
                            selectedSlot?.startTimeUTC === slot.startTimeUTC;
                          return (
                            <Pressable
                              key={slot.startTimeUTC}
                              onPress={() => {
                                setSelectedSlot(slot);
                                setRescheduleError(null);
                              }}
                              className={`rounded-full px-4 py-2 border ${
                                isSelected
                                  ? "bg-teachLoop-accentPrimary border-teachLoop-accentPrimary"
                                  : "bg-white border-gray-300"
                              } active:opacity-80`}
                            >
                              <Text
                                className={`font-geologica-medium font-medium text-sm leading-[1.5] ${
                                  isSelected ? "text-white" : "text-gray-800"
                                }`}
                              >
                                {fmt(slot.startTimeUTC, "HH:mm")}
                              </Text>
                            </Pressable>
                          );
                        })}
                      </ScrollView>

                      {selectedSlot && (
                        <Text className="font-geologica-regular font-normal text-gray-600 text-sm leading-[1.5]">
                          New time: {fmt(selectedSlot.startTimeUTC, "HH:mm")} -{" "}
                          {fmt(selectedSlot.endTimeUTC, "HH:mm")}
                        </Text>
                      )}

                      {rescheduleError && (
                        <Text className="font-geologica-regular font-normal text-red-500 text-sm leading-[1.5]">
                          {rescheduleError}
                        </Text>
                      )}

                      <View className="flex-row gap-3">
                        <Button
                          size="sm"
                          outline
                          onPress={() => {
                            setShowReschedule(false);
                            setShowConfirmation(false);
                            setSelectedSlot(null);
                            setRescheduleError(null);
                          }}
                        >
                          <Button.Text size="sm" intent="primary">
                            Cancel
                          </Button.Text>
                        </Button>

                        <Button
                          size="sm"
                          className="px-6"
                          disabled={!selectedSlot || isRescheduling}
                          onPress={handleConfirmReschedule}
                        >
                          {isRescheduling ? (
                            <ActivityIndicator size="small" color="white" />
                          ) : (
                            <Button.Text size="sm">Confirm</Button.Text>
                          )}
                        </Button>
                      </View>
                    </>
                  )}
                </View>
              )}

              {lessonData.tutorNotes && (
                <View className="gap-2">
                  <Text className="font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]">
                    Tutor Notes
                  </Text>
                  <Text className="font-geologica-regular font-normal text-black text-base leading-[1.5]">
                    {lessonData.tutorNotes}
                  </Text>
                </View>
              )}

              {lessonData.parentNotes && (
                <View className="gap-2">
                  <Text className="font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]">
                    Parent Notes
                  </Text>

                  <Text className="font-geologica-regular font-normal text-black text-base leading-[1.5]">
                    {lessonData.parentNotes}
                  </Text>
                </View>
              )}

              <View className="flex-row justify-between">
                <Pressable
                  className="flex-row items-center gap-[6px] active:opacity-80"
                  onPress={contactSupport}
                >
                  <ReportIcon
                    className="size-4"
                    pathClassName={"stroke-teachLoop-accentPrimary"}
                  />
                  <Text
                    className={`font-geologica-medium font-medium text-teachLoop-accentPrimary text-base leading-[1.5]`}
                  >
                    Report a problem
                  </Text>
                </Pressable>

                {(() => {
                  const fifteenMinutesAfterStart =
                    lessonData.startTime + 15 * 60 * 1000;
                  const canReportNoShow =
                    Date.now() >= fifteenMinutesAfterStart;

                  return (
                    <View className="items-end gap-1">
                      <Pressable
                        className={`flex-row items-center gap-[6px] ${canReportNoShow ? "active:opacity-80" : ""}`}
                        onPress={
                          canReportNoShow
                            ? () => {
                                close({
                                  reportStudentNoShow: true,
                                  joinLesson: false,
                                  cancelLesson: false,
                                  reportNoShow: false,
                                });
                              }
                            : undefined
                        }
                        disabled={!canReportNoShow}
                      >
                        <ReportIcon
                          className="size-4"
                          pathClassName={
                            canReportNoShow
                              ? "stroke-teachLoop-accentPrimary"
                              : "stroke-gray-400"
                          }
                        />
                        <Text
                          className={`font-geologica-medium font-medium text-base leading-[1.5] ${
                            canReportNoShow
                              ? "text-teachLoop-accentPrimary"
                              : "text-gray-400"
                          }`}
                        >
                          Report no show
                        </Text>
                      </Pressable>
                      {!canReportNoShow && (
                        <Text className="font-geologica-regular font-normal text-gray-400 text-xs leading-[1.5]">
                          Available 15 minutes after start time
                        </Text>
                      )}
                    </View>
                  );
                })()}
              </View>

              <View className="items-end">
                {lessonData.status === "in_progress" && (
                  <Button
                    size="sm"
                    className="px-8"
                    onPress={() => {
                      close({
                        joinLesson: true,
                        cancelLesson: false,
                        reportNoShow: false,
                        reportStudentNoShow: false,
                      });
                    }}
                  >
                    <Button.Text size="sm">Join lesson</Button.Text>
                  </Button>
                )}
              </View>

              <View className="items-end">
                {lessonData.status === "scheduled" && (
                  <Button
                    size="sm"
                    className="px-8"
                    onPress={() => {
                      close({
                        cancelLesson: true,
                        joinLesson: false,
                        reportNoShow: false,
                        reportStudentNoShow: false,
                      });
                    }}
                    outline
                  >
                    <Button.Text size="sm" intent="primary">
                      Cancel lesson
                    </Button.Text>
                  </Button>
                )}
              </View>
            </View>
            <View className="h-8" />
          </View>
        </BottomSheetScrollView>
      )}
    </BottomSheet>
  ) : null;
}

export default TutorLessonSheet;
